// LifecycleBar — the scan's stage timeline (queued → scanning → adjudicating →
// reconciling), segment widths proportional to wall-clock, duration under each.
import { theme } from "antd";
import { fmtDuration, type VMLifecycle } from "@/lib/api/scanReport";

// Each stage gets a distinct, calm hue from the theme ramp — this is a neutral
// progress readout, not a severity signal, so it must NOT reuse the red/amber/
// green severity channel.
const STAGE_TONE: Record<string, keyof ReturnType<typeof theme.useToken>["token"]> = {
  queued: "colorTextQuaternary",
  scanning: "colorPrimary",
  adjudicating: "colorInfoBorderHover",
  reconciling: "colorWarningBorderHover",
};

export default function LifecycleBar({ lifecycle }: { lifecycle: VMLifecycle }) {
  const { token } = theme.useToken();
  const { segments, totalMs } = lifecycle;
  if (!segments.length) return <div className="text-sm text-text-muted">No stage timing recorded.</div>;

  return (
    <div>
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height: 20, background: token.colorFillQuaternary }}
      >
        {segments.map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${fmtDuration(s.ms)}`}
            style={{
              width: `${(s.ms / totalMs) * 100}%`,
              background: token[STAGE_TONE[s.key]] as string,
              minWidth: 2,
            }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: token[STAGE_TONE[s.key]] as string }}
              aria-hidden
            />
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">{s.label}</span>
            <span className="tabular-nums font-medium text-text-primary">{fmtDuration(s.ms)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
